/**
 * When a scheduled trigger fires, in Temporal's shape but without Temporal.
 *
 * flow-api stores this as sent and hands it to Temporal, so the names here are Temporal's:
 * calendars for "every Monday at 9am", intervals for "every four hours", and cron expressions for
 * anything either of those cannot say. flow-api asserts at compile time that the day and month
 * names below still match the ones Temporal accepts.
 */
import { isTruthy } from 'notable-common';
import { z } from 'zod';
export const FLOW_SCHEDULE_DAYS_OF_WEEK = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
];
export const FLOW_SCHEDULE_MONTHS = [
    'JANUARY',
    'FEBRUARY',
    'MARCH',
    'APRIL',
    'MAY',
    'JUNE',
    'JULY',
    'AUGUST',
    'SEPTEMBER',
    'OCTOBER',
    'NOVEMBER',
    'DECEMBER',
];
/** Milliseconds, or the shorthand Temporal takes for them, such as `1h` or `30m`. */
export const FlowScheduleDurationSchema = z.union([z.number(), z.string()]);
/**
 * One calendar field: a single value, a range, or several of either, with `*` for all of them.
 *
 * A range carrying `step` must also carry `end`, since stepping towards an unsaid end means
 * nothing.
 */
function buildCalendarFieldSchema(unitSchema) {
    const range = z.union([
        unitSchema,
        z.object({ start: unitSchema, end: unitSchema, step: z.number() }),
        z.strictObject({ start: unitSchema, end: unitSchema.optional() }),
    ]);
    return z.union([z.literal('*'), range, z.array(range)]).optional();
}
export const FlowScheduleCalendarSpecSchema = z.object({
    second: buildCalendarFieldSchema(z.number()),
    minute: buildCalendarFieldSchema(z.number()),
    hour: buildCalendarFieldSchema(z.number()),
    dayOfMonth: buildCalendarFieldSchema(z.number()),
    month: buildCalendarFieldSchema(z.enum(FLOW_SCHEDULE_MONTHS)),
    year: buildCalendarFieldSchema(z.number()),
    dayOfWeek: buildCalendarFieldSchema(z.enum(FLOW_SCHEDULE_DAYS_OF_WEEK)),
    comment: z.string().optional(),
});
export const FlowScheduleIntervalSpecSchema = z.object({
    every: FlowScheduleDurationSchema.describe('How long between runs.'),
    offset: FlowScheduleDurationSchema.describe('How far into each interval to run.').optional(),
});
export const FlowScheduleSpecSchema = z
    .object({
    calendars: z.array(FlowScheduleCalendarSpecSchema).optional(),
    intervals: z.array(FlowScheduleIntervalSpecSchema).optional(),
    cronExpressions: z.array(z.string()).optional(),
    skip: z
        .array(FlowScheduleCalendarSpecSchema)
        .describe('Times to pass over, such as holidays.')
        .optional(),
    startAt: z.iso.datetime().describe('Do not run before this.').optional(),
    endAt: z.iso.datetime().describe('Do not run after this.').optional(),
    jitter: FlowScheduleDurationSchema.describe('Random delay added to each run.').optional(),
    timezone: z.string().describe('IANA timezone the calendars are read in.').optional(),
})
    .refine((spec) => isTruthy(spec.calendars?.length) ||
    isTruthy(spec.intervals?.length) ||
    isTruthy(spec.cronExpressions?.length), { message: 'At least one of calendars, intervals, or cronExpressions must be provided' });
//# sourceMappingURL=scheduleSpec.js.map